import React, { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../../utils/axiosConfig";
import { BsCheckCircleFill } from "react-icons/bs";
import { GoClockFill, GoXCircleFill } from "react-icons/go";
import { BsBoxSeam } from "react-icons/bs";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import './DeliveryBoy.css';

const STATS = (totalOrders, delivered, outForDelivery, cancelled) => [
  {
    label: "Total Orders",
    value: totalOrders,
    icon: <BsBoxSeam />,
    colorClass: "db-stat-icon-primary",
  },
  {
    label: "Delivered",
    value: delivered,
    icon: <BsCheckCircleFill />,
    colorClass: "db-stat-icon-success",
  },
  {
    label: "Out for Delivery",
    value: outForDelivery,
    icon: <GoClockFill />,
    colorClass: "db-stat-icon-warning",
  },
  {
    label: "Cancelled",
    value: cancelled,
    icon: <GoXCircleFill />,
    colorClass: "db-stat-icon-danger",
  },
];

const STATUS_COLOR = {
  Delivered: "#10b981",
  Cancelled: "#ef4444",
  "Out for Delivery": "#f59e0b",
  Return: "#ef4444",
};

const cardVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4, ease: "easeOut" },
  }),
};

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const customer = localStorage.getItem("customer");
        if (!customer) { setLoading(false); return; }
        const { _id: userId } = JSON.parse(customer);
        if (!userId) { setLoading(false); return; }
        const res = await axios.get(`${base_url}orders/delvery-boy/orders/${userId}`);
        setOrders(res.data.orders || []);
      } catch (err) {
        console.error("Dashboard fetch error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const totalOrders    = orders.length;
  const delivered      = orders.filter((o) => o.orderStatus === "Delivered").length;
  const outForDelivery = orders.filter((o) => o.orderStatus === "Out for Delivery").length;
  const cancelled      = orders.filter((o) => o.orderStatus === "Cancelled").length;

  const statusCounts = orders.reduce((acc, o) => {
    acc[o.orderStatus] = (acc[o.orderStatus] || 0) + 1;
    return acc;
  }, {});

  const stats = STATS(totalOrders, delivered, outForDelivery, cancelled);

  return (
    <div>
      {/* Page header */}
      <div className="db-page-header d-flex align-items-center justify-content-between">
        <div>
          <h1 className="db-page-title">Welcome Back! 👋</h1>
          <p className="db-page-subtitle">Here's a snapshot of your delivery activity today.</p>
        </div>
        <Link to="assigned-orders" className="db-btn-primary text-decoration-none d-none d-sm-inline-flex">
          View Orders →
        </Link>
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 16 }}>
          <div className="db-spinner" />
          <p style={{ color: 'var(--db-text-secondary)', fontWeight: 600, margin: 0 }}>Loading your dashboard…</p>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="row g-3 mb-4">
            {stats.map((stat, i) => (
              <div className="col-6 col-lg-3" key={i}>
                <motion.div
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={cardVariants}
                  className="db-stat-card"
                >
                  <div className={`db-stat-icon ${stat.colorClass}`}>{stat.icon}</div>
                  <div className="db-stat-label">{stat.label}</div>
                  <div className="db-stat-value">{stat.value}</div>
                  <span className="db-trend-badge">+0% vs last week</span>
                </motion.div>
              </div>
            ))}
          </div>

          {/* Status overview */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="db-card"
          >
            <div className="db-card-header">
              <h2 className="db-card-title">Order Status Overview</h2>
              <Link to="assigned-orders" style={{ fontSize: 13, fontWeight: 600, color: 'var(--db-primary)', textDecoration: 'none' }}>
                View All →
              </Link>
            </div>

            {Object.keys(statusCounts).length === 0 ? (
              <div className="db-empty-state">
                <p className="db-empty-state-title">No order data yet</p>
                <p className="db-empty-state-sub">Orders assigned to you will appear here.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: 20 }}>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div key={status} className="db-status-chip">
                    <div
                      className="db-status-dot"
                      style={{ background: STATUS_COLOR[status] || '#94a3b8' }}
                    />
                    <div>
                      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--db-text-secondary)' }}>
                        {status}
                      </p>
                      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--db-text-primary)' }}>
                        {count}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
